import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../firebase/config';

const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => isDev && console.log(...args);

const VerifyEmail = () => {
  const {
    currentUser,
    sendEmailVerification,
    verifyAndContinue,
    restaurantId,
    needsOnboarding,
    logout
  } = useAuth();

  const [status, setStatus] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const [countdown, setCountdown] = useState(0);
  const [autoVerifying, setAutoVerifying] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Si el usuario ya está verificado, redirigir
  useEffect(() => {
    if (currentUser?.emailVerified) {
      devLog('Email ya verificado, redirigiendo...');

      if (needsOnboarding) {
        navigate('/onboarding', { replace: true });
      } else if (restaurantId) {
        navigate(`/${restaurantId}`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [currentUser, needsOnboarding, restaurantId, navigate]);

  // Si viene desde el link de verificación de Firebase, procesar automáticamente
  useEffect(() => {
    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');

    // Si tiene los parámetros de Firebase para verificación de email
    if (mode === 'verifyEmail' && oobCode && !autoVerifying && !isChecking) {
      devLog('Usuario viene desde link de verificación de Firebase, procesando...');
      setAutoVerifying(true);
      handleFirebaseVerification(oobCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Countdown para reenvío
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !canResend) {
      setCanResend(true);
    }
  }, [countdown, canResend]);

  const handleFirebaseVerification = async (oobCode) => {
    try {
      devLog('Aplicando código de verificación de Firebase...');
      setStatus('Verificando tu correo...');

      // Aplicar el código de verificación de Firebase
      await applyActionCode(auth, oobCode);

      devLog('Código aplicado exitosamente, verificando usuario...');

      // Ahora verificar y continuar
      const verified = await verifyAndContinue();

      if (verified) {
        setStatus('¡Email verificado correctamente! Redirigiendo...');
        toast.success('¡Email verificado exitosamente!');

        // Redirigir inmediatamente
        if (needsOnboarding) {
          devLog('→ Redirigiendo a onboarding');
          navigate('/onboarding', { replace: true });
        } else if (restaurantId) {
          devLog('→ Redirigiendo a dashboard');
          navigate(`/${restaurantId}`, { replace: true });
        } else {
          devLog('→ Redirigiendo a root');
          navigate('/', { replace: true });
        }
      } else {
        setStatus('Error al verificar el correo. Por favor intenta de nuevo.');
        toast.error('Error en la verificación');
        setAutoVerifying(false);
      }
    } catch (error) {
      devLog('Error al aplicar código de verificación:', error);

      let errorMessage = 'Error al verificar el correo';

      if (error.code === 'auth/invalid-action-code') {
        errorMessage = 'El enlace de verificación ha expirado o ya fue usado. Por favor solicita uno nuevo.';
      } else if (error.code === 'auth/expired-action-code') {
        errorMessage = 'El enlace de verificación ha expirado. Por favor solicita uno nuevo.';
      }

      setStatus('❌ ' + errorMessage);
      toast.error(errorMessage);
      setAutoVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setStatus('Enviando correo...');
    const result = await sendEmailVerification();

    if (result.success) {
      toast.success(result.message);
      setStatus('✅ Correo enviado exitosamente');
      setCanResend(false);
      setCountdown(60); // 60 segundos de espera
    } else {
      toast.error(result.message);
      setStatus('❌ ' + result.message);
    }
  };

  const handleVerified = async () => {
    if (isChecking || autoVerifying) return;

    setIsChecking(true);
    setStatus('Verificando estado del correo...');

    try {
      const verified = await verifyAndContinue();
      devLog('Resultado de verificación:', verified);

      if (verified) {
        setStatus('¡Email verificado correctamente! Redirigiendo...');
        toast.success('¡Email verificado exitosamente!');

        // No resetear isChecking en éxito — mantener botón deshabilitado hasta que la navegación se complete
        if (needsOnboarding) {
          devLog('→ Redirigiendo a onboarding');
          navigate('/onboarding', { replace: true });
        } else if (restaurantId) {
          devLog('→ Redirigiendo a dashboard');
          navigate(`/${restaurantId}`, { replace: true });
        } else {
          devLog('→ Redirigiendo a root');
          navigate('/', { replace: true });
        }
      } else {
        setStatus(
          'El correo aún no aparece como verificado. ' +
          'Revisa tu bandeja de entrada y spam, haz click en el enlace, ' +
          'y vuelve a intentar.'
        );
        toast.error('Email aún no verificado');
        setIsChecking(false);
      }
    } catch (error) {
      devLog('Error en verificación:', error);
      setStatus('Error al verificar el correo. Inténtalo de nuevo.');
      toast.error('Error al verificar');
      setIsChecking(false);
    }
  };

  const handleBackToLogin = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      devLog('Error al cerrar sesión:', error);
      toast.error('Error al cerrar sesión');
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center">
        <div className="text-center">
          <div className="kumia-spinner mb-4">
            <div className="spinner-ring"></div>
            <div className="spinner-text">
              <span className="text-content-primary">Kum</span>
              <span className="text-kumia-green">IA</span>
            </div>
          </div>
          <p className="text-content-muted">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-app flex items-center justify-center p-6">
      <div className="max-w-lg w-full bg-surface-base border border-line-subtle rounded-2xl p-8 text-content-primary">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-kumia-green/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-kumia-green"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>

          <h1 className="text-3xl font-bold mb-2">
            Verifica tu correo
          </h1>
          <p className="text-content-muted">
            Hemos enviado un correo de verificación a:
          </p>
          <p className="text-kumia-green font-semibold mt-2">
            {currentUser.email}
          </p>
        </div>

        {/* Instructions */}
        {!autoVerifying && (
          <div className="bg-surface-3/50 rounded-lg p-4 mb-6">
            <p className="text-sm text-content-secondary mb-2">
              📧 <strong>Paso 1:</strong> Abre tu correo y busca el email de KumIA
            </p>
            <p className="text-sm text-content-secondary mb-2">
              🔗 <strong>Paso 2:</strong> Haz click en el enlace de verificación
            </p>
            <p className="text-sm text-content-secondary">
              ✅ <strong>Paso 3:</strong> Vuelve aquí y haz click en "Ya verifiqué"
            </p>
          </div>
        )}

        {/* Actions */}
        {autoVerifying ? (
          <div className="bg-kumia-green/20 border border-kumia-green/50 rounded-lg p-6 flex flex-col items-center justify-center gap-3">
            <div className="w-12 h-12 border-4 border-kumia-green/30 border-t-kumia-green rounded-full animate-spin"></div>
            <p className="text-kumia-green font-semibold">Verificando tu correo...</p>
            <p className="text-content-muted text-sm">Espera un momento mientras confirmamos tu email</p>
          </div>
        ) : (
          <div className="space-y-3">
            <button
              onClick={handleVerified}
              disabled={isChecking}
              className="w-full px-6 py-3 bg-kumia-green text-black font-semibold rounded-lg hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isChecking ? (
                <>
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                  Verificando...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Ya verifiqué mi correo
                </>
              )}
            </button>

            <button
              onClick={handleResend}
              disabled={!canResend || isChecking}
              className="w-full px-6 py-3 bg-surface-3 text-content-primary font-semibold rounded-lg hover:bg-surface-5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {countdown > 0 ? (
                `Espera ${countdown}s para reenviar`
              ) : (
                '📨 Reenviar correo de verificación'
              )}
            </button>
          </div>
        )}

        {/* Back to Login */}
        {!autoVerifying && (
          <div className="mt-4 text-center">
            <button
              onClick={handleBackToLogin}
              disabled={isChecking}
              className="text-sm text-content-muted hover:text-kumia-green transition-colors underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Volver al inicio de sesión
            </button>
          </div>
        )}

        {/* Status Message */}
        {status && (
          <div className={`mt-4 p-4 rounded-lg ${status.includes('✅') || status.includes('verificado correctamente')
            ? 'bg-green-50 dark:bg-green-900/30 border border-green-700'
            : status.includes('❌')
              ? 'bg-red-50 dark:bg-red-900/30 border border-red-700'
              : 'bg-surface-3 border border-line-strong'
            }`}>
            <p className="text-sm text-content-secondary">{status}</p>
          </div>
        )}

        {/* Help Text */}
        {!autoVerifying && (
          <div className="mt-6 text-center text-sm text-content-muted">
            <p>¿No recibiste el correo? Revisa tu carpeta de spam</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
